import { defineStore } from "pinia";
import { useFetch } from "#app";
import type { components } from "~/types_backend";

/*
useFetch, useAsyncData and $fetch.
In a nutshell: 
useFetch is the most straightforward way to handle data fetching in a component setup function. 
$fetch is great to make network requests based on user interaction. 
useAsyncData, combined with $fetch, offers more fine-grained control. 

Both useFetch and useAsyncData share a common set of options and patterns that we will detail in the last sections. 

/auth/  rest_framework.routers.APIRootView      api-root
/auth/\.<format>/       rest_framework.routers.APIRootView      api-root
/auth/jwt/create/       rest_framework_simplejwt.views.TokenObtainPairView      jwt-create
/auth/jwt/refresh/      rest_framework_simplejwt.views.TokenRefreshView jwt-refresh
/auth/jwt/verify/       rest_framework_simplejwt.views.TokenVerifyView  jwt-verify
/auth/users/    djoser.views.UserViewSet        user-list
/auth/users/<id>/       djoser.views.UserViewSet        user-detail
/auth/users/<id>\.<format>/     djoser.views.UserViewSet        user-detail
/auth/users/activation/ djoser.views.UserViewSet        user-activation
/auth/users/activation\.<format>/       djoser.views.UserViewSet        user-activation
/auth/users/me/ djoser.views.UserViewSet        user-me
/auth/users/me\.<format>/       djoser.views.UserViewSet        user-me
/auth/users/resend_activation/  djoser.views.UserViewSet        user-resend-activation
/auth/users/resend_activation\.<format>/        djoser.views.UserViewSet        user-resend-activation 
/auth/users/reset_password/     djoser.views.UserViewSet        user-reset-password
/auth/users/reset_password\.<format>/   djoser.views.UserViewSet        user-reset-password
/auth/users/reset_password_confirm/     djoser.views.UserViewSet        user-reset-password-confirm    
/auth/users/reset_password_confirm\.<format>/   djoser.views.UserViewSet        user-reset-password-confirm
/auth/users/reset_username/     djoser.views.UserViewSet        user-reset-username
/auth/users/reset_username\.<format>/   djoser.views.UserViewSet        user-reset-username
/auth/users/reset_username_confirm/     djoser.views.UserViewSet        user-reset-username-confirm    
/auth/users/reset_username_confirm\.<format>/   djoser.views.UserViewSet        user-reset-username-confirm
/auth/users/set_password/       djoser.views.UserViewSet        user-set-password
/auth/users/set_password\.<format>/     djoser.views.UserViewSet        user-set-password
/auth/users/set_username/       djoser.views.UserViewSet        user-set-username
/auth/users/set_username\.<format>/     djoser.views.UserViewSet        user-set-username
/auth/users\.<format>/  djoser.views.UserViewSet        user-list
*/

export const useUserStore = defineStore("user", {
  state: () => ({
    user: null as components["schemas"]["CustomUserOutput"] | null,
    isAuthenticated: false,
    jwtToken: null as { access: string; refresh: string } | null,
  }),
  actions: {
    async fetchMyUser() {
      try {
        const data = (await $fetch("/api/auth/users/me/", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${this.jwtToken?.access}`,
          },
        })) as components["schemas"]["CustomUserOutput"];

        this.user = data; // Directly assign the fetched data to `user`
      } catch (error: Error | any) {
        console.error("Fetch user failed:", error.message ?? error);
        this.resetState();
      }
    },

    async login(username: string, password: string) {
      try {
        const data = (await $fetch("/api/auth/jwt/create/", {
          method: "POST",
          body: JSON.stringify({ username, password }),
        })) as { access: string; refresh: string };

        this.jwtToken = data;
        this.isAuthenticated = true;

        const authCookie = useCookie("jwtToken");
        authCookie.value = JSON.stringify(data);

        await this.fetchMyUser();

        return this.isAuthenticated;
      } catch (error: Error | any) {
        console.error("Login failed:", error.message ?? error);
        this.resetState();

        return error;
      }
    },
    async logout() {
      try {
        const { error } = (await useFetch("/api/auth/jwt/refresh/", {
          method: "POST",
          body: JSON.stringify({ refresh: this.jwtToken?.refresh }),
        })) as {
          error: any;
        };

        if (error.value !== null) {
          throw new Error(error.value);
        }

        this.resetState();
      } catch (error) {
        console.error("Logout failed:", error);
        this.resetState();
      }
    },
    async performLocalLogin(cookieToken?: { access: string; refresh: string }) {
      if (cookieToken) {
        this.jwtToken = cookieToken;
      } else {
        console.error("No JWT token provided");
        this.resetState();
        return false;
      }

      try {
        const data = (await $fetch("/api/auth/jwt/refresh/", {
          method: "POST",
          body: JSON.stringify({ refresh: this.jwtToken?.refresh }),
        })) as { access: string };

        if (!data.access) {
          throw new Error("JWT token refresh failed");
        }

        this.jwtToken.access = data.access;

        await this.fetchMyUser();
        this.isAuthenticated = true;
        return true;
      } catch (error) {
        console.error("JWT token refresh failed:", error);
        this.resetState();
        return false;
      }
    },

    resetState() {
      console.info("Resetting user state");
      this.user = null;
      this.isAuthenticated = false;
      this.jwtToken = null;

      try {
        localStorage.removeItem("jwtToken");
      } catch (error) {
        console.error("Failed to remove JWT token from local storage:", error);
      }
      const authCookie = useCookie("jwtToken");
      authCookie.value = null;
    },
    async setPassword(currentPassword: string, newPassword: string) {
      const { error } = (await apiPostRequest("/api/auth/users/set_password/", {
        current_password: currentPassword,
        new_password: newPassword,
        re_new_password: newPassword,
      })) as {
        error: any;
      };

      if (error !== null) {
        return error.data;
      }

      return null;
    },
    async register(
      username: string,
      email: string,
      password: string,
      re_password: string
    ) {
      const { error } = (await useFetch("/api/auth/users/", {
        method: "POST",
        body: JSON.stringify({ username, email, password, re_password }),
      })) as {
        error: any;
      };

      if (error !== null && error.value !== null) {
        return error.value.data;
      }

      return null;
    },

    /* Email Verification */
    async createAndSendEmailVerification() {
      const { success, error } = await apiPostRequest(
        "/api/usermanagement/create_and_send_email_verification/",
        {}
      );

      if (error !== null) {
        return error.data;
      }

      return success;
    },
    async verifyEmailToken(emailToken: string, email: string) {
      const result = await $fetch("/api/usermanagement/verify_email_token/", {
        method: "POST",
        body: JSON.stringify({
          email_verification_token: emailToken,
          email,
        }),
        ignoreResponseError: true,
      });

      return result;
    },
  },
});
